import { createContext, useContext } from 'react';

// -----------------------------------------------------------------------------
// The login UI in @ow/shared is presentational. App-specific auth & profile
// actions, validators and config are supplied by each app through this adapter,
// provided via <LoginAdapterContext.Provider>. Cross-cutting concerns that are
// not login-specific (translation, analytics, legal links) are passed straight
// to <LoginSignupForm> as props from each app's own hooks/config instead.
// -----------------------------------------------------------------------------

export type LoginCloseType = 'success' | 'skip';

export enum SocialNetwork {
  Discord = 'Discord',
  Facebook = 'Facebook',
  Gfycat = 'Gfycat',
  Google = 'Google',
  None = 'None',
  Reddit = 'Reddit',
  Twitch = 'Twitch',
  Twitter = 'Twitter',
  VKontakte = 'VKontakte',
  Youtube = 'Youtube',
}

// Member order matches both apps' validator enums so the numeric `reason` values
// returned by either app's validators line up with these constants.
export enum ValidationReason {
  Empty,
  Invalid,
  InvalidLength,
}

export enum UserValidationReason {
  Empty,
  InvalidLength,
  Invalid,
  Taken,
}

export interface SignUpParams {
  username: string;
  email: string;
  password: string;
  captchaV2Response: any;
  sendEmails: boolean;
}

export interface ILoginResult {
  status: string;
  error?: string;
  token?: string;
}

export interface SignUpResult {
  success: boolean;
  error?: any;
  token?: string;
}

export interface IResetPasswordResult {
  success: boolean;
  statusCode?: number;
}

// Structural validator results — typed with a plain `number` reason so each app's
// own (nominally distinct) enum results are assignable without casts.
export interface ValidationResult {
  isValid: boolean;
  reason?: number;
}

export interface SharedProfile {
  username?: string;
  email?: string;
  avatar?: string;
  nickname?: string;
  suspended?: boolean;
}

export interface SharedCurrentUser {
  profile: SharedProfile;
  token?: string;
}

export interface LoginAdapter {
  // --- profile state ---
  currentUser: SharedCurrentUser | null;
  loginStateChangedEvent: any | null;
  // website exposes profile.unverified, ow-app exposes profile.verified
  isUnverified: (user: SharedCurrentUser | null) => boolean;

  // --- actions (shared form always passes the PLAIN password; the adapter
  //     hashes it where its backend requires) ---
  performOverwolfLogin: (email: string, password: string) => Promise<ILoginResult>;
  performOverwolfSignUp: (params: SignUpParams) => Promise<SignUpResult>;
  performSocialLogin: (
    network: SocialNetwork,
  ) => Promise<ILoginResult | void> | void;
  resetPassword: (email: string) => Promise<IResetPasswordResult>;
  cancelAccountDeletion: () => Promise<unknown>;
  setToken: (token: string) => unknown;
  getAndSetProfile?: () => Promise<{ suspended?: boolean } | undefined>;
  logout: () => unknown;
  resendVerification: (email: string) => Promise<boolean>;

  // --- validators ---
  validateUsername: (
    username: string,
    validateTaken?: boolean,
  ) => Promise<ValidationResult>;
  validateEmail: (email: string) => ValidationResult;
  validatePassword: (password: string, isLogin: boolean) => ValidationResult;

  // --- services / config ---
  recaptchaSiteKey: string;
  // Prefix for static login images. Empty on the website (same-origin
  // /static_next/...); the absolute site origin in the Overwolf app so the
  // images resolve off the website rather than the local shell host.
  assetBaseUrl: string;
}

// -----------------------------------------------------------------------------
export const LoginAdapterContext = createContext<LoginAdapter | null>(null);

// -----------------------------------------------------------------------------
export const useLoginAdapter = (): LoginAdapter => {
  const adapter = useContext(LoginAdapterContext);
  if (!adapter) {
    throw new Error(
      'useLoginAdapter must be used within a <LoginAdapterContext.Provider>',
    );
  }
  return adapter;
};
